import { ContentExpander, Grid, NumberField, Spacer } from '@components';
import { useClearRCSettingsError, useRateCardModifiersState } from '@hooks';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import {
	AllRateCardFuelLevyModifiers, AllRateCardSettingFuelLevyModifiers, RateCardFormErrors, RateCardSettingsTypes, RateCardTypes
} from '@state';
import {
	RateCardType,
	TRateCardModifierSet,
	TRUCK_TYPE,
	TTruckTypeModifierDetails
} from '@types';
import { getRateCardIndex, getTruckTypeLabel, removeLeadingZeros } from '@utils';
import { useRecoilValue } from 'recoil';
import styles from './Modifiers.module.scss';
import { ModifierBadge } from './ModifierBadge';

type TProps = {
	isSettings?: boolean;
	type: RateCardType
	isDisabled?: boolean;
	itemTypeId?: string;
}

export const TrailerTypeModifierPanel = (props: TProps) => {
	const {
		isSettings,
		type,
		isDisabled = false,
		itemTypeId
	} = props;
	const [rateCardModifiers, setRateCardModifiers] = useRateCardModifiersState();
	const modifiers = isSettings ? rateCardModifiers.settings : rateCardModifiers.rateCard;

	const rateCardSettingsTypes = useRecoilValue(RateCardSettingsTypes);
	const rateCardTypes = useRecoilValue(RateCardTypes);
	const rateCardTypeIndex = getRateCardIndex({
		rateCardSettingsTypes,
		rateCardTypes,
		itemTypeId,
		isSettings: !!isSettings,
		type
	});

	const errorString = isSettings ? 'rate_card_types' : 'rate_card_prices';
	const rcFormErrors = useRecoilValue(RateCardFormErrors);
	const clearRCSettingsError = useClearRCSettingsError();

	const allRateCardModifiers = useRecoilValue(AllRateCardFuelLevyModifiers);
	const allRateCardSettingModifiers = useRecoilValue(AllRateCardSettingFuelLevyModifiers);
	const fuelLevyModifiers = isSettings ? allRateCardSettingModifiers : allRateCardModifiers;

	const updateTrailerTypeModifier = (
		trailerType: keyof TTruckTypeModifierDetails,
		newValue: string,
	) => {
		const update = {
			...modifiers,
			truck_type: {
				details: {
					...modifiers?.truck_type.details,
					[trailerType]: {
						type: 'FIXED',
						value: newValue
					},
				},
				attracts_fuel_levy: !!fuelLevyModifiers?.truck_type?.attracts_fuel_levy
			},
		} as TRateCardModifierSet;
		const newModifiers = {
			settings: isSettings ? update : undefined,
			rateCard: !isSettings ? update : undefined
		}
		setRateCardModifiers(newModifiers);
		clearRCSettingsError(`${errorString}.${rateCardTypeIndex}.modifier_set.truck_type.details.${trailerType}.value`)
	}

	const handleOnBlur = (
		trailerType: keyof TTruckTypeModifierDetails,
		newValue: string
	) => {
		if (newValue.startsWith('0') && !newValue.startsWith('0.')) {
			const value = removeLeadingZeros(newValue);
			updateTrailerTypeModifier(trailerType, value);
		}
		if (newValue.startsWith('.')) {
			updateTrailerTypeModifier(trailerType, `0${newValue}`)
		}
	}

	// ERROR HANDLING
	const hasError = (trailerType: keyof TTruckTypeModifierDetails) => {
		// @ts-ignore
		return rcFormErrors?.[errorString]?.[rateCardTypeIndex]?.modifier_set?.truck_type?.details?.[trailerType]?.value?._errors?.[0] || '';
	}

	// ERROR HANDLING
	const getSectionErrors = () => {
		const fieldsWithPossibleError = Object.values(TRUCK_TYPE);
		return fieldsWithPossibleError.map((field) =>
			hasError(field as keyof TTruckTypeModifierDetails)
		).some((field) => field.length > 0);
	}

	const getConfiguredCount = () => {
		const details = modifiers?.truck_type?.details;
		if (!details) return 0;
		return Object.values(TRUCK_TYPE).reduce((count, truck) => {
			if (truck === TRUCK_TYPE.ANY) return count;
			const detail = details[truck as keyof TTruckTypeModifierDetails];
			return parseFloat(`${detail?.value ?? ''}`) > 0 ? count + 1 : count;
		}, 0);
	}

	if (!modifiers) return

	return (
		<ContentExpander
			title={<div className={styles.expanderTitle}>Trailer type
				<ModifierBadge count={getConfiguredCount()} />
				{
					getSectionErrors() ?
						<span className={styles.expanderErrorContainer}>
							<ErrorOutlineOutlinedIcon />
						</span>	: null
				}
			</div>}
			expanded={false}
			arrowsAsExpanderIcon
			iconColor='var(--neutral-06)'
		>
			<div className={styles.expanderContent}>
				<Grid columns={2} gap={'16px'} alignItems='start'>
					{
						Object.values(TRUCK_TYPE).map((truck) => {
							if (truck === TRUCK_TYPE.ANY) return null;
							const modifierDetails = modifiers?.truck_type?.details;
							const realValue = modifierDetails ? modifierDetails[truck as keyof TTruckTypeModifierDetails]?.value : '';
							return (
								<NumberField
									key={truck}
									name={truck}
									value={realValue}
									onChange={(e) => updateTrailerTypeModifier(truck as keyof TTruckTypeModifierDetails, e.target.value)}
									leftModule={<span className={styles.prefix}>$</span>}
									label={getTruckTypeLabel(truck)}
									keepErrorSpace={false}
									errorMessage={hasError(truck)}
									hasError={!!hasError(truck)}
									disabled={isDisabled}
									min={'0'}
									onBlur={(e) => handleOnBlur(truck as keyof TTruckTypeModifierDetails, e.target.value)}
								/>
							)
						})
					}
				</Grid>
				<Spacer size={5} />
			</div>
		</ContentExpander>
	);
}